/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN INVESTMENT TRANSACTIONS API
 * Role: Admin
 * Scope: Tenant-based Investment Transaction & Repayment Ledger
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

const ADMIN_INVESTMENT_TRANSACTIONS_ENDPOINT = "/admin/Investors/investment_transactions.php";

export const getAdminInvestmentTransactions = async (
  { 
    page = 1, 
    limit = 10, 
    search = "", 
    transaction_type = "", 
    status = "", 
    investment_id = "", 
    investor_id = "", 
    branch_id = "" 
  } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_INVESTMENT_TRANSACTIONS_ENDPOINT,
      null,
      "Admin – Fetch Investment Transactions",
      {
        params: { 
          page, 
          limit, 
          search, 
          transaction_type, 
          status, 
          investment_id, 
          investor_id, 
          branch_id 
        },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch investment transactions",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error("Admin Fetch Investment Transactions Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

export const getAdminInvestmentTransactionById = async (id, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Invalid transaction ID" };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_INVESTMENT_TRANSACTIONS_ENDPOINT}?id=${id}`,
      null,
      "Admin – Fetch Investment Transaction By ID",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to fetch transaction record" };
    }

    return { success: true, data: res.data };
  } catch (error) {
    console.error("Admin Fetch Investment Transaction By ID Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred" };
  }
};

export const createAdminInvestmentTransaction = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_INVESTMENT_TRANSACTIONS_ENDPOINT,
      payload,
      "Admin – Create Investment Transaction",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to record transaction" };
    }

    return { success: true, data: res.data, message: res.message };
  } catch (error) {
    console.error("Admin Create Investment Transaction Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred" };
  }
};

export const updateAdminInvestmentTransaction = async (id, payload, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Invalid transaction ID" };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_INVESTMENT_TRANSACTIONS_ENDPOINT}?id=${id}`,
      payload,
      "Admin – Update Investment Transaction",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to update transaction record" };
    }

    return { 
      success: true, 
      data: res.data || null, 
      message: res.message || "Transaction updated successfully" 
    };
  } catch (error) {
    console.error("Admin Update Investment Transaction Error:", error);
    return { success: false, message: error?.message || "Unexpected error occurred" };
  }
};

export const deleteAdminInvestmentTransaction = async (id, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Invalid transaction ID" };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_INVESTMENT_TRANSACTIONS_ENDPOINT}?id=${id}`,
      null,
      "Admin – Delete Investment Transaction",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to delete transaction record" };
    }

    return { success: true, message: res.message };
  } catch (error) {
    console.error("Admin Delete Investment Transaction Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred" };
  }
};